import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Rating,
  Chip,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { ShoppingCart, Favorite, FavoriteBorder } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [favorite, setFavorite] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  const imageUrl = product.image ? `${API_URL}${product.image}` : '/placeholder-product.jpg';

  const handleAddToCart = async (e) => {
    e.stopPropagation();

    try {
      await addToCart(product.id, 1, {
        name: product.name,
        price: product.price,
        image: product.image,
        sku: product.sku
      });
      setSnackbar({ 
        open: true, 
        message: 'Product added to cart!', 
        severity: 'success' 
      });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to add to cart', 
        severity: 'error' 
      });
    }
  };

  const handleCardClick = () => {
    navigate(`/products/${product.id}`);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    setFavorite(!favorite);
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          },
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height="200"
            image={imageUrl}
            alt={product.name}
            sx={{ objectFit: 'cover' }}
            onError={(e) => {
              e.target.src = '/placeholder-product.jpg';
            }}
          />
          
          {product.is_new && (
            <Chip
              label="NEW"
              color="success"
              size="small"
              sx={{ position: 'absolute', top: 8, left: 8 }}
            />
          )}
          
          {product.is_featured && (
            <Chip
              label="FEATURED"
              color="primary"
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
          )}

          <IconButton
            onClick={handleFavoriteClick}
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'white',
              '&:hover': { bgcolor: 'grey.100' },
            }}
            size="small"
          >
            {favorite ? <Favorite color="error" /> : <FavoriteBorder />}
          </IconButton>
        </Box>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            gutterBottom
            variant="body2"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}
          >
            {product.category_name || 'Uncategorized'}
          </Typography>

          <Typography
            gutterBottom
            variant="h6"
            component="div"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: '3em',
            }}
          >
            {product.name}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Rating value={product.average_rating || 0} precision={0.5} size="small" readOnly />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({product.review_count || 0})
            </Typography>
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ${product.price}
              </Typography>
              {product.compare_at_price && product.compare_at_price > product.price && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textDecoration: 'line-through' }}
                >
                  ${product.compare_at_price}
                </Typography>
              )}
            </Box>

            {product.stock_quantity > 0 ? (
              <Button
                variant="contained"
                fullWidth
                startIcon={<ShoppingCart />}
                onClick={handleAddToCart}
              >
                Add to Cart
              </Button>
            ) : (
              <Button variant="outlined" fullWidth disabled>
                Out of Stock
              </Button>
            )}

            {product.stock_quantity > 0 && product.stock_quantity <= 5 && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                Only {product.stock_quantity} left in stock!
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductCard;
